import type { BotDifficulty } from '@cribbage/bots';
import {
  ENGINE_VERSION,
  type GameConfig,
  type GameResult,
  type Move,
  type Seat,
} from '@cribbage/engine';
import { REPLAY_FORMAT_VERSION } from '@cribbage/protocol';
import type { OfflineGameUpload, OfflineSyncResponse } from '@cribbage/protocol';
import { ApiError, api } from '~/api/client.js';
import {
  LOCAL_FORMAT_VERSION,
  type LocalGameKind,
  type LocalGameRecord,
  markSettled,
  markSynced,
  pendingSync,
  updateLocalGame,
} from '~/store/localGames.js';
import { playerId, useAuthStore } from '~/store/authStore.js';
import { useSettingsStore } from '~/store/settingsStore.js';
import { flight } from '~/session/singleFlight.js';

/**
 * Getting offline games logged, and finished ones counted.
 *
 * Two halves. `recordLocalGame` stores the game on the device after every move,
 * always — that is what makes local history and local replay work, and it
 * happens for pass-and-play too. `syncPendingGames` is the part that talks to
 * the server, and it is more restrained: vs-bot only, opt-out-able, and only
 * for a game with a player stamped on it.
 *
 * Every player has a session, anonymous or not, so there is one upload route
 * and no guest door beside it. What decides whose game it is, is the player id
 * written onto the record when the game was played — never who holds the token
 * when the sync happens.
 *
 * A game is offered while it is still being played, not only once it ends. A
 * replay is `(config, seed, moves)` and moves only ever append, so each push is
 * the same game further along and the server brings its row up to date. That is
 * what makes a grid inspectable before the match around it is over — which is
 * the whole reason to keep the log at all.
 *
 * The upload carries `(config, seed, moves)` and no results. Everything stored
 * server-side is recomputed by re-running the moves through the engine, so
 * there is nothing here for a modified client to inflate. See
 * `docs/STATISTICS.md` §3 and §4.
 */

export interface RecordedLocalGame {
  readonly kind: LocalGameKind;
  readonly config: GameConfig;
  readonly seed: string;
  readonly moves: readonly { seat: Seat; move: Move }[];
  readonly seatNames: readonly string[];
  readonly botDifficulties: readonly (BotDifficulty | null)[];
  /** The signed-in player's seat, or null when nobody can be credited. */
  readonly mySeat: Seat | null;
  readonly startedAt: string;
  /** Null while the game is still being played. */
  readonly result: GameResult | null;
  /** The id to write under. Omit for a new game; pass it back to update one. */
  readonly id?: string | undefined;
}

/**
 * Crypto-strength where available; a game id does not need to be unguessable.
 *
 * Exported because the caller has to mint it *before* the first save resolves.
 * A screen that waited for the id to come back would start a second game on the
 * second move, since the first write is still in flight when it lands.
 */
export function newLocalGameId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const rand = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, '0');
  return `${rand()}${rand()}-${rand()}-4${rand().slice(1)}-a${rand().slice(1)}-${rand()}${rand()}${rand()}`;
}

/** What `recordLocalGame` answers when another tab has moved the same game on. */
export const PLAYED_ELSEWHERE = 'played-elsewhere';

/**
 * Store a game on the device, new or further along. Returns the stored record,
 * null if there is nothing in it worth keeping, or `PLAYED_ELSEWHERE` when the
 * stored game has gone on without these moves and the save was refused.
 *
 * The id is the caller's to keep: one game keeps one id from its first move to
 * its last, because that id is what the server dedupes and updates on.
 *
 * The sync bookkeeping is carried through from whatever is on disk at the
 * moment of the write, not from a copy read before it. `updateLocalGame` is
 * what makes those the same instant: a sync acknowledgement is doing the mirror
 * of this — reading the moves and writing the bookkeeping — and the two
 * overlapping unguarded lose one side's work every time.
 */
export async function recordLocalGame(
  game: RecordedLocalGame,
): Promise<LocalGameRecord | null | typeof PLAYED_ELSEWHERE> {
  if (game.moves.length === 0) return null;

  const id = game.id ?? newLocalGameId();
  /*
   * Whose game it is, decided at the first save and never again.
   *
   * Read here rather than at sync time, which is the fix for the attribution
   * bug: a game played signed out and synced after a sign-in used to land in
   * the account. `existing` is what keeps it decided *once*. A game is written
   * after every move, so re-reading the store on each save would hand a match
   * that spans a sign-in to whoever finished it, which is the same bug moved
   * from the sync to the last move.
   */
  const owner = playerId() ?? '';
  let refused = false;
  const saved = await updateLocalGame(id, (existing) => {
    /*
     * A save carries the whole move log, so it may only extend the one on disk.
     * A resumed game keeps its record id, and a second tab resuming the same
     * game is a second writer with a log of its own. Without this the slower
     * tab replaced the longer game, or a finished one, with whatever it had.
     * A finished game is never written back as one still being played.
     */
    const unfinishes = existing?.result != null && game.result === null;
    if (existing !== null && (unfinishes || !extendsLog(existing.moves, game.moves))) {
      refused = true;
      return null;
    }
    return {
      id,
      formatVersion: LOCAL_FORMAT_VERSION,
      engineVersion: existing === null ? ENGINE_VERSION : (existing.engineVersion ?? '1.0.0'),
      playerId: existing?.playerId ?? owner,
      kind: game.kind,
      config: game.config,
      seed: game.seed,
      moves: game.moves,
      seatNames: game.seatNames,
      botDifficulty: game.botDifficulties.find((difficulty) => difficulty !== null) ?? null,
      // Pass-and-play is never creditable, whoever is holding the phone.
      mySeat: game.kind === 'bot' ? game.mySeat : null,
      startedAt: game.startedAt,
      finishedAt: new Date().toISOString(),
      result: game.result,
      syncedMoveCount: existing?.syncedMoveCount ?? 0,
      syncedAt: existing?.syncedAt ?? null,
      accepted: existing?.accepted ?? false,
    };
  });
  return refused ? PLAYED_ELSEWHERE : saved;
}

/** Does `next` start with every move in `stored`, in order? */
function extendsLog(stored: readonly unknown[], next: readonly unknown[]): boolean {
  return (
    next.length >= stored.length &&
    stored.every((entry, index) => canonical(entry) === canonical(next[index]))
  );
}

/** A move as data, keys sorted, so two copies of one move always read the same. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, part: unknown) =>
    part !== null && typeof part === 'object' && !Array.isArray(part)
      ? Object.fromEntries(Object.entries(part).sort(([a], [b]) => a.localeCompare(b)))
      : part,
  );
}

/**
 * Turn a stored game into an upload, or null if it is not one to send.
 *
 * Note what it does not carry: no score, no winner, no stats. The server
 * recomputes all of that from the moves, so there is nothing here for a
 * modified client to inflate.
 */
export function toUpload(record: LocalGameRecord): OfflineGameUpload | null {
  if (record.mySeat === null || record.playerId === '') return null;
  return {
    client_uuid: record.id,
    /*
     * Who played it. The server accepts this when it is the session's own
     * player, or an anonymous player that has held a session on this device
     * before, which is what lets a game played signed out still go up after a
     * sign-in without changing hands.
     */
    player_id: record.playerId,
    /*
     * How the moves are encoded, not how the record on the device is shaped.
     * The two shared one number until `LOCAL_FORMAT_VERSION` had to move for a
     * new field, which says nothing about the replay the server stores.
     */
    format_version: REPLAY_FORMAT_VERSION,
    engine_version: record.engineVersion ?? '1.0.0',
    config: record.config,
    seed: record.seed,
    moves: record.moves.map((entry) => ({ seat: entry.seat, move: entry.move })),
    seat_names: [...record.seatNames],
    /*
     * Every seat, the player's included, and never a null.
     *
     * Rails runs `deep_munge` over an incoming array and drops its nil entries,
     * so a positional `[null, 'hard']` arrives as `['hard']` and the bot's seat
     * reads empty. Nothing errors: the admin just showed every hard bot as
     * "Bot (medium)". Sending no nulls keeps the positions intact, and the
     * server ignores whatever sits at the uploader's own seat anyway.
     */
    bot_difficulties: record.seatNames.map(() => record.botDifficulty),
    my_seat: record.mySeat,
    started_at: record.startedAt,
    finished_at: record.finishedAt,
  };
}

/**
 * The server's answer to `wrong_player`: this signer may not file this game.
 *
 * The one rejection reason that is about *who is asking* rather than about the
 * game. Everything else in `rejected` is a statement about the log itself.
 */
const WRONG_PLAYER = 'wrong_player';
/* The server's engine cannot score the rules this log was recorded under. That
 * is a fact about which build is answering, not about the log, so it is held
 * like `wrong_player` rather than settled — see the `held` note below. */
const UNSUPPORTED_VERSION = 'unsupported_engine_version';
const HELD_REASONS: readonly string[] = [WRONG_PLAYER, UNSUPPORTED_VERSION];

/**
 * What the server said, split by what to do about it.
 *
 * `settled` is the new shape and the important one: the server has these games
 * up to the moves we just sent, which for a game still in play is progress
 * rather than a conclusion. Whether that settles the game is decided per
 * record, by whether the game has a result and the answer covers every move it
 * holds — see `acknowledged`.
 *
 * A duplicate is a game the server considers finished with, so it is read too.
 * `refused` means the log will never verify, and re-offering it every launch
 * would be a request that fails forever.
 *
 * `held` is the third answer, and it is the one a shared device needs.
 * `wrong_player` says the session that asked is not allowed to file this game:
 * it was played by an anonymous player who has not held a session on this
 * device from the server's point of view, or the account signed in now is
 * simply not them. That is a fact about the signer, not about the game, and it
 * stops being true the moment the right player is signed in. Settling it would
 * throw the game away over a temporary answer, so nothing is written and it
 * goes out again next time. Only `deferred` — nobody was home — and `held` are
 * worth another try unchanged.
 *
 * `unsupported_engine_version` is held for the same reason. It says the engine
 * answering cannot score the rules this log was recorded under — a fact about
 * which build is deployed, not about the log — and it stops being true once the
 * server catches up. Settling it would permanently drop a Cross game that was
 * already on the device when the rules changed, and would do the same to a
 * current game whose version a lagging Rails had stripped on the way through.
 */
export function settledFrom(response: OfflineSyncResponse): {
  readonly settled: string[];
  readonly refused: string[];
  readonly held: string[];
} {
  const held = response.rejected.filter((entry) => HELD_REASONS.includes(entry.reason));
  return {
    settled: [...response.accepted, ...response.duplicates],
    refused: response.rejected
      .filter((entry) => !HELD_REASONS.includes(entry.reason))
      .map((entry) => entry.client_uuid),
    held: held.map((entry) => entry.client_uuid),
  };
}

export interface SyncOutcome {
  readonly counted: number;
  readonly dropped: number;
  /**
   * The server took the request and could not decide yet: the verifier was
   * unreachable, or the time budget for the batch ran out. Nothing is settled,
   * and the same games go out again on the next run.
   */
  readonly deferred: number;
  /**
   * Records this signer was not allowed to file — the server's `wrong_player`.
   *
   * Its own number rather than part of `deferred`, because the two clear
   * differently. A deferred game lands on the next attempt; a held one waits
   * for the player who owns it to sign in on this device, which may never
   * happen. Folding them together made a permanent state read as a temporary
   * one, and the sign-in below treats those two answers as opposites.
   */
  readonly held: number;
  /**
   * The run never reached the server, or the server refused it.
   *
   * A drain that counted nothing and a drain that could not happen at all look
   * identical in the numbers above, and they mean opposite things: the first
   * says the device has nothing owed, the second says it has no idea. A caller
   * that has to know whether the games are up — the sign-in, before it asks
   * what there is to import — reads this rather than the zero.
   */
  readonly failed: boolean;
}

const IDLE: SyncOutcome = { counted: 0, dropped: 0, deferred: 0, held: 0, failed: false };
const FAILED: SyncOutcome = { counted: 0, dropped: 0, deferred: 0, held: 0, failed: true };

/** One sync at a time: sign-in and game-end can both ask within a second. */
const inFlight = flight<SyncOutcome>();
/** The follow-up owed to whoever asked while a request was already out. */
let queued: Promise<SyncOutcome> | null = null;

/**
 * Send whatever is waiting. Safe to call whenever — it decides for itself
 * whether there is anything to do.
 *
 * Never throws: a sync that cannot happen is not an error a player should be
 * interrupted by, and the games stay on the device for the next attempt.
 */
export function syncPendingGames(): Promise<SyncOutcome> {
  const running = inFlight.current();
  if (running === null) return inFlight.join(run);
  /*
   * A request in the air covers the moves that went out with it and nothing
   * after them, so handing this caller that promise answers the wrong question
   * — and answers it with somebody else's outcome. The finishing move is the
   * case that matters: it asks for a sync while the previous move's push is
   * still out, so it is owed the run that carries the move that ended the game,
   * not the one that went without it.
   *
   * One follow-up serves everybody who arrives during a run, because they are
   * all waiting on the same next request.
   */
  queued ??= running
    .catch(() => undefined)
    .then(() => {
      queued = null;
      return syncPendingGames();
    });
  return queued;
}

/**
 * Offer the device's games whenever the token changes, and once on the way in.
 *
 * A sign-in *swaps* one token for another, so subscribing to `null -> token`
 * missed the case that matters most: the games played on this device signed out
 * are exactly what the one-time import offer is about, and they have to be on
 * the server before anybody asks whether there is anything to import.
 *
 * `syncPendingGames` decides for itself whether there is anything to do — it
 * declines for pass-and-play, for a game with no player stamped on it, and for
 * anyone who has turned it off — so subscribing to every change costs nothing
 * when there is nothing to send. Returns the unsubscribe.
 */
export function syncOnTokenChange(): () => void {
  let previous = useAuthStore.getState().token;
  if (previous !== null) void syncPendingGames();

  return useAuthStore.subscribe((state) => {
    const changed = state.token !== previous;
    previous = state.token;
    if (changed && state.token !== null) void syncPendingGames();
  });
}

/** Long enough that a run of quick placements is one request, short enough that
    the admin copy is never far behind the board. */
const SYNC_DEBOUNCE_MS = 2000;

/*
 * The upload gets its own deadline because `inFlight` is only cleared in run()'s
 * `.finally`, and everything else chains behind it: the sign-in drain races a
 * five-second timer but does not abort the request, so a stalled upload went on
 * holding the lane long after the UI had moved on, and every later foreground
 * retry, game-end sync and token change queued behind it. Generous enough that a
 * slow connection finishes an evening's games, short enough that a hung request
 * settles rather than parking the queue until the browser gives up on its own.
 */
const UPLOAD_TIMEOUT_MS = 20_000;
// OfflineGames::Ingest accepts at most 25 games per request.
const UPLOAD_BATCH_SIZE = 25;

let pendingTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Ask for a sync soon, or right now.
 *
 * A game is offered after every move, and a placement takes a second or two, so
 * without this the device would open a request per move for the length of a
 * match. `now` is for the moment a game ends, where waiting two more seconds to
 * file the finished thing is the one case with nothing to gain.
 */
export function scheduleSync({ now = false }: { now?: boolean } = {}): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (now) {
    void syncPendingGames();
    return;
  }
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    void syncPendingGames();
  }, SYNC_DEBOUNCE_MS);
}

async function run(): Promise<SyncOutcome> {
  if (!useSettingsStore.getState().syncOfflineGames) return IDLE;

  /*
   * No session, so try to get one before giving up.
   *
   * A device with no token has not registered, which means the launch that
   * should have registered it had no network. Waiting for a reload would leave
   * an evening of games stamped with nobody, so the sync is one of the two
   * places that retries; the other is the app coming back to the foreground.
   * `restore` is guarded against overlapping calls and mints at most one device.
   */
  if (useAuthStore.getState().token === null) {
    await useAuthStore.getState().restore();
    // Still nobody to send as. The games are owed and nothing went up, which is a
    // failed drain rather than an empty one.
    if (useAuthStore.getState().token === null) return FAILED;
  }

  let outcome: SyncOutcome = IDLE;
  const token = useAuthStore.getState().token;
  try {
    const waiting = await pendingSync();
    if (waiting.length === 0) return IDLE;

    const uploads = waiting.map(toUpload).filter((upload) => upload !== null);
    if (uploads.length === 0) return IDLE;

    // Walk this snapshot once. Held or deferred games remain pending, but must
    // not occupy the first batch again while older games are still owed a turn.
    for (let offset = 0; offset < uploads.length; offset += UPLOAD_BATCH_SIZE) {
      if (
        useAuthStore.getState().token !== token ||
        !useSettingsStore.getState().syncOfflineGames
      ) {
        return { ...outcome, failed: true };
      }
      const batch = uploads.slice(offset, offset + UPLOAD_BATCH_SIZE);
      // Acknowledgments cover exactly the moves in this request.
      const sent = new Map(batch.map((upload) => [upload.client_uuid, upload.moves.length]));
      const response = await api.syncOfflineGames(batch, AbortSignal.timeout(UPLOAD_TIMEOUT_MS));
      const { settled, refused, held } = settledFrom(response);
      await markSynced(new Map(settled.map((id) => [id, sent.get(id) ?? 0])));
      await markSettled(refused, false);
      outcome = {
        counted: outcome.counted + response.accepted.length,
        dropped: outcome.dropped + refused.length,
        deferred: outcome.deferred + response.deferred.length,
        held: outcome.held + held.length,
        failed: false,
      };
    }
    // The final acknowledgment can arrive after sign-out or an opt-out too.
    // Keep that receipt, but do not let its caller treat a changed session as drained.
    return {
      ...outcome,
      failed:
        useAuthStore.getState().token !== token || !useSettingsStore.getState().syncOfflineGames,
    };
  } catch (error) {
    /*
     * Signed out mid-sync, or no network. Either way the games are still here —
     * and the caller is told the drain failed rather than that it found nothing,
     * because those two answers lead somewhere different.
     */
    if (error instanceof ApiError || error instanceof Error) return { ...outcome, failed: true };
    throw error;
  }
}
